import { Injectable, signal } from '@angular/core';
import {
  ELEMENT_TYPE_TO_MAP_LAYER,
  MapLayerEnum,
  MapLayerGroupEnum,
  SectionLabelsType,
} from '@construction/construction.enums';
import { WbsElementLabelDictionary } from '@construction/models/construction-project.model';

import DigitalTwinLayersJson from '../../../assets/js/digital-twin-structure.json';
import { ElementType } from '@construction/modules/qc-issues/qc-issues.enums';

export enum LayerTypeEnum {
  Group = 'group',
  Element = 'element',
}

export enum LayerElementTypeEnum {
  Civil = 'civil',
  Mechanical = 'mechanical',
  Electrical = 'electrical',
  Substation = 'substation',
  Group = 'group',
}

export type MapLayerStyle = {
  size: string;
  lineStyle: string;
  color: string;
};

export type MapLayerConfiguration = {
  key: MapLayerEnum;
  layerType: LayerTypeEnum;
  elementType?: LayerElementTypeEnum;
  labels: WbsElementLabelDictionary;
  style: MapLayerStyle;
  min_zoom?: number;
};

@Injectable({
  providedIn: 'root',
})
export class LayerConfigurationService {
  public ElementLayerMap = new Map([
    [MapLayerEnum.SITE, ElementType.Site],
    [MapLayerEnum.PCS, ElementType.Pcs],
    [MapLayerEnum.PIERS, ElementType.Pier],
    [MapLayerEnum.MODULES, ElementType.Module],
    [MapLayerEnum.TRACKERS, ElementType.Tracker],
    [MapLayerEnum.SUBSTATIONS, ElementType.Substation],
    [MapLayerEnum.BESS, ElementType.Bess],
    [MapLayerEnum.AC_STATIONS, ElementType.AcStation],
    [MapLayerEnum.CBX, ElementType.Cbx],
    // DT3 substation sub-elements. The backend QC create/get flow does not accept per-sub-element
    // element_types yet, so for now every sub-element is reported as the classic `substation`
    // (element_id still points at the specific sub-element that was clicked). The per-sub-element
    // ElementType values are kept in the enum (ControlBuildingFoundation, Mpt, OtherFoundation, ...),
    // ready to swap back in here once the backend supports per-sub-element observations.
    [MapLayerEnum.CONTROL_BUILDING_FOUNDATION, ElementType.Substation],
    [MapLayerEnum.CONTROL_BUILDING, ElementType.Substation],
    [MapLayerEnum.GROUND_GRID, ElementType.Substation],
    [MapLayerEnum.MPT_FOUNDATION, ElementType.Substation],
    [MapLayerEnum.MPT, ElementType.Substation],
    [MapLayerEnum.CAPACITOR_BANK, ElementType.Substation],
    [MapLayerEnum.CAPACITOR_BANK_FOUNDATION, ElementType.Substation],
    [MapLayerEnum.OTHER_FOUNDATION, ElementType.Substation],
    [MapLayerEnum.HI_SIDE_BREAKER, ElementType.Substation],
    [MapLayerEnum.LO_SIDE_BREAKER, ElementType.Substation],
  ]);

  public elementTypeMap: Map<string, MapLayerEnum> = new Map([
    [ElementType.Site as string, MapLayerEnum.SITE],
    [ElementType.Pcs as string, MapLayerEnum.PCS],
    [ElementType.Area as string, MapLayerEnum.AREA],
    [ElementType.MvCircuit as string, MapLayerEnum.FEEDERS],
    [ElementType.CombinerBox as string, MapLayerEnum.COMBINER_BOXES],
    [ElementType.Pier as string, MapLayerEnum.PIERS],
    [ElementType.Tracker as string, MapLayerEnum.TRACKERS],
    [ElementType.Module as string, MapLayerEnum.MODULES],
    [ElementType.Substation as string, MapLayerEnum.SUBSTATIONS],
    [ElementType.Bess as string, MapLayerEnum.BESS],
    [ElementType.AcStation as string, MapLayerEnum.AC_STATIONS],
    [ElementType.Cbx as string, MapLayerEnum.CBX],
  ]);

  public readonly sectionLabels = signal<SectionLabelsType>({
    group: LayerElementTypeEnum.Group,
    civil: LayerElementTypeEnum.Civil,
    mechanical: LayerElementTypeEnum.Mechanical,
    electrical: LayerElementTypeEnum.Electrical,
    substation: LayerElementTypeEnum.Substation,
  });

  public getLayerConfig(layerKey: MapLayerEnum): MapLayerConfiguration {
    const layers: MapLayerConfiguration[] = JSON.parse(JSON.stringify(DigitalTwinLayersJson));
    const layerConfig = layers.find((layer) => layer.key === layerKey);
    if (!layerConfig) {
      throw new Error(`Layer ${layerKey} not found in configuration`);
    }

    return layerConfig;
  }

  public getAllLayerConfigs(): MapLayerConfiguration[] {
    const layers: MapLayerConfiguration[] = JSON.parse(JSON.stringify(DigitalTwinLayersJson));

    return layers;
  }

  public getLayerConfigByElementType(elementType: LayerElementTypeEnum): MapLayerConfiguration[] {
    const layers: MapLayerConfiguration[] = JSON.parse(JSON.stringify(DigitalTwinLayersJson));
    const layerConfigs = layers.filter((layer) => layer.elementType === elementType);

    return layerConfigs;
  }

  public getLayerConfigByLayerType(layerType: LayerTypeEnum): MapLayerConfiguration[] {
    const layers: MapLayerConfiguration[] = JSON.parse(JSON.stringify(DigitalTwinLayersJson));
    const layerConfigs = layers.filter((layer) => layer.layerType === layerType);

    return layerConfigs;
  }

  /**
   * DT3's `dictionary_of_labels` and `weights` key EVERY element by its DT3 node type name
   * (singular — `hole`, `pier`, `project`, `feeder`, `combiner_box`, `boundary`, ...), which for
   * most elements is not the DT2 field spelling (`holes`, `piers`, `sites`, `feeders`,
   * `combiner_boxes`, `fences`, ...) and not the plural MapLayerEnum value used for the map/tile
   * layer either. This is the single source of truth for that translation, shared by
   * `getDictionaryKey`/`getWeightKey` since DT3 keys labels and weights identically — unlike DT2,
   * which spells some group elements differently between the two (see the DT2 maps below).
   */
  private static readonly DT3_NODE_TYPE_KEY: Record<MapLayerEnum, string> = {
    [MapLayerEnum.AREA]: 'area',
    [MapLayerEnum.FEEDERS]: 'feeder',
    [MapLayerEnum.COMBINER_BOXES]: 'combiner_box',
    [MapLayerEnum.PCS]: 'pcs',
    [MapLayerEnum.SITE]: 'project',
    // A DT3 substation is both a hierarchy group and an installable element. Both roles use the
    // same backend node-type key; the map-facing layer remains `substations`.
    [MapLayerEnum.SUBSTATION_GROUP]: 'substation',

    [MapLayerEnum.ROADS]: 'road',
    [MapLayerEnum.FENCES]: 'boundary',
    [MapLayerEnum.MVT_FENCES]: 'boundary',

    [MapLayerEnum.HOLES]: 'hole',
    [MapLayerEnum.PIERS]: 'pier',
    [MapLayerEnum.TABLES]: 'table',
    [MapLayerEnum.TRACKERS]: 'tracker',
    [MapLayerEnum.MODULES]: 'module',

    [MapLayerEnum.SUBSTATIONS]: 'substation',
    [MapLayerEnum.CBX]: 'cbx',
    [MapLayerEnum.DC_HARNESSES]: 'dc_harness',
    [MapLayerEnum.DC_FEEDERS]: 'dc_feeder',
    [MapLayerEnum.MV_FEEDERS]: 'mv_feeder',
    [MapLayerEnum.AC_STATIONS]: 'ac_station',
    [MapLayerEnum.BESS]: 'bess',

    [MapLayerEnum.CONTROL_BUILDING_FOUNDATION]: 'control_building_foundation',
    [MapLayerEnum.CONTROL_BUILDING]: 'control_building',
    [MapLayerEnum.GROUND_GRID]: 'ground_grid',
    [MapLayerEnum.MPT_FOUNDATION]: 'mpt_foundation',
    [MapLayerEnum.MPT]: 'mpt',
    [MapLayerEnum.CAPACITOR_BANK]: 'capacitor_bank',
    [MapLayerEnum.CAPACITOR_BANK_FOUNDATION]: 'capacitor_bank_foundation',
    [MapLayerEnum.OTHER_FOUNDATION]: 'other_foundation',
    [MapLayerEnum.HI_SIDE_BREAKER]: 'hi_side_breaker',
    [MapLayerEnum.LO_SIDE_BREAKER]: 'lo_side_breaker',

    [MapLayerEnum.QC_ISSUE]: MapLayerEnum.QC_ISSUE,
  };

  /** The `dictionary_of_labels` key a layer's labels are stored under. DT2-keyed unless `isDt3Enabled`. */
  public getDictionaryKey(layerType: MapLayerEnum, isDt3Enabled = false): string {
    if (isDt3Enabled) {
      return LayerConfigurationService.DT3_NODE_TYPE_KEY[layerType];
    }

    const map: Record<MapLayerEnum, string> = {
      [MapLayerEnum.AREA]: 'area',
      [MapLayerEnum.FEEDERS]: 'mv_circuit',
      [MapLayerEnum.COMBINER_BOXES]: 'combiner_box_group',
      [MapLayerEnum.PCS]: 'ac_block',
      [MapLayerEnum.SITE]: 'site',
      [MapLayerEnum.SUBSTATION_GROUP]: MapLayerEnum.SUBSTATION_GROUP,

      [MapLayerEnum.ROADS]: MapLayerEnum.ROADS,
      [MapLayerEnum.FENCES]: MapLayerEnum.FENCES,
      [MapLayerEnum.MVT_FENCES]: MapLayerEnum.MVT_FENCES,

      [MapLayerEnum.HOLES]: MapLayerEnum.HOLES,
      [MapLayerEnum.PIERS]: MapLayerEnum.PIERS,
      [MapLayerEnum.TABLES]: MapLayerEnum.TABLES,
      [MapLayerEnum.TRACKERS]: MapLayerEnum.TRACKERS,
      [MapLayerEnum.MODULES]: MapLayerEnum.MODULES,

      [MapLayerEnum.SUBSTATIONS]: MapLayerEnum.SUBSTATIONS,
      [MapLayerEnum.CBX]: MapLayerEnum.CBX,
      [MapLayerEnum.DC_HARNESSES]: MapLayerEnum.DC_HARNESSES,
      [MapLayerEnum.DC_FEEDERS]: MapLayerEnum.DC_FEEDERS,
      [MapLayerEnum.MV_FEEDERS]: MapLayerEnum.MV_FEEDERS,
      [MapLayerEnum.AC_STATIONS]: MapLayerEnum.AC_STATIONS,
      [MapLayerEnum.BESS]: MapLayerEnum.BESS,

      // Not reachable from DT2 projects (the Substation settings section is DT3-only), listed
      // for the Record's exhaustiveness check only.
      [MapLayerEnum.CONTROL_BUILDING_FOUNDATION]: 'control_building_foundation',
      [MapLayerEnum.CONTROL_BUILDING]: 'control_building',
      [MapLayerEnum.GROUND_GRID]: 'ground_grid',
      [MapLayerEnum.MPT_FOUNDATION]: 'mpt_foundation',
      [MapLayerEnum.MPT]: MapLayerEnum.MPT,
      [MapLayerEnum.CAPACITOR_BANK]: 'capacitor_bank',
      [MapLayerEnum.CAPACITOR_BANK_FOUNDATION]: 'capacitor_bank_foundation',
      [MapLayerEnum.OTHER_FOUNDATION]: 'other_foundation',
      [MapLayerEnum.HI_SIDE_BREAKER]: 'hi_side_breaker',
      [MapLayerEnum.LO_SIDE_BREAKER]: 'lo_side_breaker',

      [MapLayerEnum.QC_ISSUE]: MapLayerEnum.QC_ISSUE,
    };

    return map[layerType];
  }

  /** The `weights` key a layer's weight is stored under. DT2-keyed (== the MapLayerEnum value) unless `isDt3Enabled`. */
  public getWeightKey(layerType: MapLayerEnum, isDt3Enabled = false): string {
    return isDt3Enabled ? LayerConfigurationService.DT3_NODE_TYPE_KEY[layerType] : layerType;
  }

  /**
   * The path segment an element-detail endpoint expects for a layer. DT2 uses the plural layer
   * value (`trackers`, `piers`, ...); DT3 uses the singular node type (`tracker`, `pier`, ...),
   * sharing the same source of truth as the label/weight keys.
   */
  public getElementEndpointSegment(layerType: MapLayerEnum | string, isDt3Enabled = false): string {
    return isDt3Enabled
      ? (LayerConfigurationService.DT3_NODE_TYPE_KEY[layerType as MapLayerEnum] ?? layerType)
      : layerType;
  }

  public selectElementNameDataSource(group: string) {
    switch (group) {
      case MapLayerGroupEnum.CIVIL:
        return 'Segment';
      case MapLayerGroupEnum.ELECTRICAL:
        return 'Wire';
      case MapLayerGroupEnum.MECHANICAL:
        return 'Element';
      case MapLayerGroupEnum.SUBSTATION:
        return 'Element';
      default:
        return '';
    }
  }

  /** Reverse of ELEMENT_TYPE_TO_MAP_LAYER: the tile's element_type (singular) for a map layer (plural). */
  private readonly layerToElementType: Record<string, string> = Object.entries(ELEMENT_TYPE_TO_MAP_LAYER).reduce(
    (acc, [elementType, layer]) => ({ ...acc, [layer]: elementType }),
    {} as Record<string, string>,
  );

  /**
   * The singular element_type the backend expects for a (plural) map layer, e.g. `ground_grids` →
   * `ground_grid`. Falls back to the layer itself when there is no mapping.
   */
  public getElementTypeForLayer(layer: string): string {
    return this.layerToElementType[layer] ?? layer;
  }

  public getElementGroup(group: string): string[] {
    switch (group) {
      case MapLayerGroupEnum.CIVIL:
        return [MapLayerEnum.ROADS, MapLayerEnum.FENCES];
      case MapLayerGroupEnum.MECHANICAL:
        return [MapLayerEnum.PIERS, MapLayerEnum.TABLES, MapLayerEnum.TRACKERS, MapLayerEnum.MODULES];
      case MapLayerGroupEnum.ELECTRICAL:
        return [
          MapLayerEnum.CBX,
          MapLayerEnum.DC_HARNESSES,
          MapLayerEnum.DC_FEEDERS,
          MapLayerEnum.MV_FEEDERS,
          MapLayerEnum.AC_STATIONS,
          MapLayerEnum.BESS,
          MapLayerEnum.SUBSTATIONS,
        ];
      case MapLayerGroupEnum.SUBSTATION:
        return [
          MapLayerEnum.CONTROL_BUILDING_FOUNDATION,
          MapLayerEnum.CONTROL_BUILDING,
          MapLayerEnum.GROUND_GRID,
          MapLayerEnum.MPT_FOUNDATION,
          MapLayerEnum.MPT,
          MapLayerEnum.CAPACITOR_BANK,
          MapLayerEnum.CAPACITOR_BANK_FOUNDATION,
          MapLayerEnum.OTHER_FOUNDATION,
          MapLayerEnum.HI_SIDE_BREAKER,
          MapLayerEnum.LO_SIDE_BREAKER,
        ];
      case MapLayerGroupEnum.GROUP_ELEMENTS:
        return [
          MapLayerEnum.SITE,
          MapLayerEnum.PCS,
          MapLayerEnum.AREA,
          MapLayerEnum.FEEDERS,
          MapLayerEnum.COMBINER_BOXES,
        ];
      default:
        return [];
    }
  }
}
